import React from 'react';
import { Eye, Camera, Scan, Shield, CheckCircle, Zap } from 'lucide-react';
import { useSEO } from '../../hooks/useSEO';
import { Breadcrumb } from '../../components/Breadcrumb';
import { useBanner } from '../../contexts/BannerContext';

export const CognitiveVisionPage: React.FC = () => {
  useSEO('cognitive-vision' as any);
  const { isBannerVisible, bannerHeight } = useBanner();

  return (
    <>
      <Breadcrumb />
      <main className="pb-20">
      <section 
        className="px-4 sm:px-6 lg:px-8 mb-20"
        style={{ 
          paddingTop: isBannerVisible ? `${bannerHeight + 64 + 44 + 16}px` : '124px' // 64px header + 44px breadcrumb + 16px extra space
        }}
      >
        <div className="max-w-7xl mx-auto text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-700 rounded-3xl mx-auto mb-8 flex items-center justify-center">
            <Eye className="w-10 h-10 text-white" />
          </div>
          <h1 
            className="text-5xl md:text-6xl font-bold mb-8 py-4 px-2 leading-loose bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent overflow-visible min-h-fit"
            style={{ lineHeight: '1.8', minHeight: 'auto', overflow: 'visible', paddingTop: '16px', paddingBottom: '16px' }}
          >
            Cognitive Vision
          </h1>
          <p className="text-xl text-gray-700 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Advanced computer vision solutions that see, understand, and analyze visual data. 
            Transform images and videos into actionable business intelligence.
          </p>
        </div>
      </section>

      <div className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <section className="grid lg:grid-cols-2 gap-12 mb-16">
            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Vision AI Capabilities</h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Image & Video Analytics</h3>
                    <p className="text-gray-700 dark:text-gray-300">Real-time analysis of visual content with object detection and classification.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Quality Control Automation</h3>
                    <p className="text-gray-700 dark:text-gray-300">Automated inspection and defect detection in manufacturing processes.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">OCR & Document Processing</h3>
                    <p className="text-gray-700 dark:text-gray-300">Extract text and data from documents, forms, and images with high accuracy.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Facial Recognition Systems</h3>
                    <p className="text-gray-700 dark:text-gray-300">Secure access control and identity verification with advanced facial analysis.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Technology Features</h2>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <Camera className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Real-time Processing</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Instant analysis and response</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <Scan className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Multi-format Support</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Images, videos, live streams</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Privacy Compliant</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">GDPR and CCPA ready</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <Zap className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Edge Computing</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Local processing capabilities</p>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Industry Applications</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-4">Manufacturing</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Automated quality inspection, defect detection, assembly line monitoring, and predictive maintenance.
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-4">Healthcare</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Medical imaging analysis, diagnostic assistance, patient monitoring, and treatment optimization.
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-4">Retail & Security</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Customer behavior analysis, inventory management, loss prevention, and access control systems.
                </p>
              </div>
            </div>
          </section>

          <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              See Beyond Human Vision
            </h2>
            <p className="text-gray-700 dark:text-gray-300 text-lg mb-8 max-w-3xl mx-auto">
              Unlock the power of computer vision to automate visual processes, enhance quality control, 
              and gain insights from your visual data that humans simply cannot see.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.location.href = '/contact'}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                Enhance Your Vision
              </button>
              <button
                onClick={() =>
                  window.open(
                    "https://outlook.office.com/book/CognitiveCraftHub2@cognitivecrafthub.com/?ismsaljsauthenabled",
                    "_blank"
                  )
                }
                className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
              >
                Request Demo
              </button>
            </div>
          </section>
        </div>
      </div>
    </main>
    </>
  );
};